// =============================================================================
// MeuDriver — Instructor Students Management
// =============================================================================

import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { formatDate, formatCurrency, getInitials } from '@/lib/utils'
import { Users, Search, Star, BookOpen, Clock, Phone, Ban, UndoDot } from 'lucide-react'
import type { ProfileRow } from '@/types/database'

interface BlockedEntry {
  id: string
  student_id: string
  reason: string | null
  created_at: string
  student: Pick<ProfileRow, 'id' | 'full_name' | 'avatar_url' | 'city'> | null
}

interface StudentStats {
  student: ProfileRow
  totalLessons: number
  completedLessons: number
  lastLesson: string | null
  totalSpent: number
  avgRatingGiven: number | null
}

export default function InstructorStudents() {
  const { user } = useAuth()
  const [instructorId, setInstructorId] = useState<string | null>(null)
  const [students, setStudents] = useState<StudentStats[]>([])
  const [blocked, setBlocked] = useState<BlockedEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [blockDialogStudent, setBlockDialogStudent] = useState<ProfileRow | null>(null)
  const [blockReason, setBlockReason] = useState('')
  const [blockSubmitting, setBlockSubmitting] = useState(false)

  // Resolve instructor ID
  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', user.id)
      .single()
      .then(({ data }) => setInstructorId(data?.id ?? null))
  }, [user?.id])

  useEffect(() => {
    if (!instructorId) return
    loadStudents()
    loadBlocked()
  }, [instructorId])

  async function loadBlocked() {
    if (!instructorId) return
    const { data } = await supabase
      .from('instructor_blocklist')
      .select(`
        id, student_id, reason, created_at,
        student:profiles!instructor_blocklist_student_id_fkey(id, full_name, avatar_url, city)
      `)
      .eq('instructor_id', instructorId)
      .order('created_at', { ascending: false })
    setBlocked((data ?? []) as unknown as BlockedEntry[])
  }

  function openBlockDialog(student: ProfileRow) {
    setBlockDialogStudent(student)
    setBlockReason('')
  }

  async function confirmBlock() {
    if (!instructorId || !blockDialogStudent) return
    const reasonTrim = blockReason.trim()
    if (reasonTrim.length > 0 && reasonTrim.length < 3) {
      toast.error('Descreva o motivo com pelo menos 3 caracteres (ou deixe em branco).')
      return
    }
    setBlockSubmitting(true)
    const { error } = await supabase.from('instructor_blocklist').insert({
      instructor_id: instructorId,
      student_id: blockDialogStudent.id,
      reason: reasonTrim || null,
    })
    setBlockSubmitting(false)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success(`${blockDialogStudent.full_name ?? 'Aluno'} bloqueado.`)
    setBlockDialogStudent(null)
    await loadBlocked()
  }

  async function unblock(id: string, name?: string | null) {
    if (!confirm(`Desbloquear ${name ?? 'aluno'}?`)) return
    const { error } = await supabase.from('instructor_blocklist').delete().eq('id', id)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success('Aluno desbloqueado.')
    await loadBlocked()
  }

  async function loadStudents() {
    if (!instructorId) return
    setLoading(true)
    try {
      const { data: bookings } = await supabase
        .from('bookings')
        .select(`
          student_id, status, scheduled_at, total_price, duration_minutes,
          student:profiles!bookings_student_id_fkey(*)
        `)
        .eq('instructor_id', instructorId)
        .order('scheduled_at', { ascending: false })

      if (!bookings) return

      const { data: ratings } = await supabase
        .from('reviews')
        .select('student_id, rating')
        .eq('instructor_id', instructorId)

      const ratingMap: Record<string, number[]> = {}
      for (const r of ratings ?? []) {
        if (!ratingMap[r.student_id]) ratingMap[r.student_id] = []
        ratingMap[r.student_id].push(r.rating)
      }

      // Group by student
      const map: Record<string, StudentStats> = {}
      for (const b of bookings) {
        const sid = b.student_id
        const profile = (b as any).student as ProfileRow
        if (!map[sid]) {
          map[sid] = {
            student: profile,
            totalLessons: 0,
            completedLessons: 0,
            lastLesson: null,
            totalSpent: 0,
            avgRatingGiven: null,
          }
        }
        map[sid].totalLessons++
        if (b.status === 'completed') {
          map[sid].completedLessons++
          map[sid].totalSpent += b.total_price ?? 0
          if (!map[sid].lastLesson || b.scheduled_at > map[sid].lastLesson!) {
            map[sid].lastLesson = b.scheduled_at
          }
        }
      }

      for (const [sid, stats] of Object.entries(map)) {
        const arr = ratingMap[sid]
        if (arr?.length) {
          stats.avgRatingGiven = arr.reduce((a, b) => a + b, 0) / arr.length
        }
      }

      setStudents(
        Object.values(map).sort(
          (a, b) => (b.lastLesson ?? '').localeCompare(a.lastLesson ?? ''),
        ),
      )
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return students
    const q = search.toLowerCase()
    return students.filter((s) =>
      s.student?.full_name?.toLowerCase().includes(q) ||
      s.student?.city?.toLowerCase().includes(q),
    )
  }, [students, search])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-brand-600" />
            Meus alunos
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {students.length} aluno{students.length !== 1 ? 's' : ''} no total
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou cidade..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total de alunos', value: students.length, icon: Users, color: 'text-brand-600' },
          { label: 'Aulas concluídas', value: students.reduce((s, st) => s + st.completedLessons, 0), icon: BookOpen, color: 'text-teal-600' },
          { label: 'Alunos recorrentes', value: students.filter((s) => s.totalLessons > 1).length, icon: Star, color: 'text-orange-500' },
          { label: 'Receita total', value: null, formatted: formatCurrency(students.reduce((s, st) => s + st.totalSpent, 0)), icon: Clock, color: 'text-green-600' },
        ].map((item) => (
          <Card key={item.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <item.icon className={`h-8 w-8 shrink-0 ${item.color}`} />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {item.formatted ?? item.value}
                </p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs Ativos / Bloqueados */}
      <Tabs defaultValue="active">
        <TabsList className="grid grid-cols-2 max-w-xs">
          <TabsTrigger value="active">Ativos ({students.length})</TabsTrigger>
          <TabsTrigger value="blocked">Bloqueados ({blocked.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {filtered.length} aluno{filtered.length !== 1 ? 's' : ''}
            {search ? ` para "${search}"` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-4 p-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              {search ? 'Nenhum aluno encontrado.' : 'Você ainda não tem alunos.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Aluno</th>
                    <th className="px-4 py-3 font-medium">Cidade</th>
                    <th className="px-4 py-3 font-medium">Aulas</th>
                    <th className="px-4 py-3 font-medium">Total gasto</th>
                    <th className="px-4 py-3 font-medium">Última aula</th>
                    <th className="px-4 py-3 font-medium">Avaliação dada</th>
                    <th className="px-4 py-3 font-medium">Contato</th>
                    <th className="px-4 py-3 font-medium text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((s) => (
                    <tr key={s.student.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={s.student.avatar_url ?? undefined} />
                            <AvatarFallback className="text-xs font-semibold bg-brand-100 text-brand-700">
                              {getInitials(s.student.full_name ?? 'A')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{s.student.full_name ?? 'Aluno'}</p>
                            {s.totalLessons > 1 && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100">
                                Recorrente
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {s.student.city ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold">{s.completedLessons}</span>
                        <span className="text-muted-foreground text-xs ml-1">
                          /{s.totalLessons}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-green-700">
                        {formatCurrency(s.totalSpent)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {s.lastLesson ? formatDate(s.lastLesson) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {s.avgRatingGiven != null ? (
                          <span className="flex items-center gap-1">
                            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                            {s.avgRatingGiven.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {s.student.phone ? (
                          <a
                            href={`https://wa.me/55${s.student.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-2.5 py-1 transition-colors"
                          >
                            <Phone className="h-3 w-3" />
                            WhatsApp
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openBlockDialog(s.student)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1 h-7 text-xs"
                          title="Bloquear este aluno"
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Bloquear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="blocked" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Ban className="h-4 w-4 text-red-500" />
                Alunos bloqueados
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {blocked.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm">
                  Você não bloqueou nenhum aluno.
                </div>
              ) : (
                <ul className="divide-y">
                  {blocked.map((b) => (
                    <li key={b.id} className="flex items-center gap-3 px-4 py-3">
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarImage src={b.student?.avatar_url ?? undefined} />
                        <AvatarFallback className="text-xs">
                          {getInitials(b.student?.full_name ?? 'A')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {b.student?.full_name ?? 'Aluno'}
                        </p>
                        {b.reason && (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            Motivo: {b.reason}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground">
                          Bloqueado em {formatDate(b.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unblock(b.id, b.student?.full_name)}
                        className="gap-1 shrink-0"
                      >
                        <UndoDot className="h-3.5 w-3.5" />
                        Desbloquear
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog bloquear */}
      <Dialog
        open={!!blockDialogStudent}
        onOpenChange={(v) => !v && setBlockDialogStudent(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-500" />
              Bloquear {blockDialogStudent?.full_name ?? 'aluno'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              O aluno não conseguirá agendar novas aulas com você. Você pode
              desbloquear a qualquer momento na aba "Bloqueados".
            </p>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Motivo (opcional, privado)
              </label>
              <Textarea
                rows={3}
                maxLength={500}
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Ex: não compareceu em duas aulas seguidas"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogStudent(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBlock}
              disabled={blockSubmitting}
            >
              {blockSubmitting ? 'Bloqueando...' : 'Confirmar bloqueio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
